"use client";

import { useEffect, useState } from "react";
import { Card, Table, Button, Spinner, Accordion, Row, Col, Collapse } from "react-bootstrap";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { getUserRequests, approveRequest, rejectRequest, getUserBalance } from "../../services/user";

function useIsMobile(breakpointPx = 768) { // md = 768
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpointPx);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpointPx]);

  return isMobile;
}

const UserCreditRequestsComp = () => {
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  const isMobile = useIsMobile(768);
  const [showAllBalances, setShowAllBalances] = useState(false);

  // اگر از موبایل خارج شدیم، اتومات باز بماند
  useEffect(() => {
    if (!isMobile) setShowAllBalances(true);
    if (isMobile) setShowAllBalances(false); // اگر دوست دارید در ورود به موبایل دوباره بسته شود
  }, [isMobile]);

  const loadData = async () => {
    try {
      const [reqs, bal] = await Promise.all([
        getUserRequests(),
        getUserBalance(),
      ]);
      setRequests(reqs);
      setBalance(bal);
    } catch (err) {
      console.error(err);
      toast.error("خطا در بارگذاری اطلاعات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // --- تایید درخواست با پیام هشدار ---
  const handleApprove = async (id) => {
    const confirm = await Swal.fire({
      title: "تایید درخواست؟",
      text: "آیا از تایید این درخواست اطمینان دارید؟",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "بله، تایید شود",
      cancelButtonText: "انصراف",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    setActionLoading(id);
    try {
      //await approveRequest(id); // غیر فعال کردن درخواست تأیید از سمت کاربر
      toast.success("درخواست تایید شد");
      loadData();
    } catch (err) {
      console.error(err);
      toast.error("خطا در تایید درخواست");
      loadData();
    } finally {
      setActionLoading(null);
    }
  };

  // --- رد درخواست با پیام هشدار ---
  const handleReject = async (id) => {
    const confirm = await Swal.fire({
      title: "رد درخواست؟",
      text: "آیا از رد این درخواست اطمینان دارید؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "بله، رد شود",
      cancelButtonText: "انصراف",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;
    
    setActionLoading(id);
    try {
      //await rejectRequest(id); // غیر فعال کردن رد درخواست از سمت کاربر
      toast.success("درخواست رد شد");
      loadData();
    } catch (err) {
      console.error(err);
      toast.error("خطا در رد درخواست");
      loadData();
    } finally {
      setActionLoading(null);
    }
  };

  const formatJalaliMinusOneDay = (dateStr) => {
    if (!dateStr) return '';

    const d = new Date(dateStr);
    d.setDate(d.getDate() - 1);

    return d.toLocaleDateString('fa-IR');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p>در حال بارگذاری اطلاعات...</p>
      </div>
    );
  }



  const safeBalance = balance ?? {};

  const categoryBalances = Array.isArray(safeBalance.categoryBalances)
    ? safeBalance.categoryBalances
    : [];

  const shopAll = safeBalance.shop_all ?? null;
  const shopAllOld = safeBalance.shop_all_old ?? null;
  const kasreMahane = safeBalance.kasre_mahane ?? null;

  const totalWalletBalance = Number(safeBalance.totalWalletBalance ?? 0);
  const totalCreditBalance = Number(safeBalance.totalCreditBalance ?? 0);
  const walletBalance = Number(safeBalance.walletBalance ?? 0);
  const creditBalance = Number(safeBalance.creditBalance ?? 0);

  //const totalShopAll = Number(safeBalance.totalShopAll ?? 0);
  //const totalKasreMahane = Number(safeBalance.totalKasreMahane ?? 0);

  const getTxColor = (tx) => (tx?.type === "charge" ? "green" : "#bd0000");
  const getTxLabel = (tx) => (tx?.type === "charge" ? "طلب" : "بدهی");

  const parseMaybeJson = (v) => {
    if (!v) return null;
    if (typeof v === "object") return v; // اگر بک‌اند آبجکت داد
    try { return JSON.parse(v); } catch { return null; }
  };

  const totalShopAll = Number(shopAll?.amount ?? 0) + categoryBalances
    .filter(c => c.lastShopAll)
    .reduce((sum, c) => {
      const tx = parseMaybeJson(c.lastShopAll);
      const amount = tx && tx.amount ? Number(tx.amount) : 0;
      return sum + amount;
    }, 0);
  const totalKasreMahane = Number(kasreMahane?.amount ?? 0) + categoryBalances
    .filter(c => c.lastKasreMahane)
    .reduce((sum, c) => {
      const tx = parseMaybeJson(c.lastKasreMahane);
      const amount = tx && tx.amount ? Number(tx.amount) : 0;
      return sum + amount;
    }, 0);

  const formatJalali = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);

    // اگر تاریخ نامعتبر بود
    if (Number.isNaN(d.getTime())) return "—";

    return <span className="d-inline-block" style={{direction:'ltr'}}>
      {
        d.toLocaleString("fa-IR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          //second: "2-digit", // اگر ثانیه هم خواستی
          hour12: false,
        })
      }
    </span>;
  };



  return (
    <>
      <div className="container-fluid p-0 mb-5">
        {/* بخش اول: کیف پول و اعتبار */}
        <h5 className="fw-bold mb-3 mt-4 text-dark">جزئیات کیف پول و اعتبار</h5>
        <Row className="g-3">

          {/* همیشه نمایش داده می‌شود */}
          {/* کارت مجموع */}
          <Col
            xs={12} md={4} lg={3} xxl={2}
            role={isMobile ? "button" : undefined}
            style={isMobile ? { cursor: "pointer" } : undefined}
            onClick={isMobile ? () => setShowAllBalances(v => !v) : undefined}
          >
            <div className="p-3 shadow-sm card" style={{ background: '#312E81', color: '#fff' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="opacity-75 fw-bold fs-6">مجموع کل</small>
                <i className="material-symbols-outlined fs-40">account_balance_wallet</i>
              </div>
              <div className="fs-6">کیف پول: {Number(totalWalletBalance).toLocaleString()} تومان</div>
              <div className="fs-6">اعتبار: {Number(totalCreditBalance).toLocaleString()} تومان</div>
              {isMobile && (
                <small className="d-block text-end text-light">
                  {showAllBalances ? "مخفی کردن" : "نمایش جزئیات"}
                </small>
              )}
            </div>
          </Col>

          {/* بقیه کارت‌ها: در دسکتاپ همیشه باز / در موبایل toggle */}
          <Collapse
            in={!isMobile || showAllBalances}
            appear={false}
            mountOnEnter
            unmountOnExit
            className="mt-0"
          >
            <div className="w-100">
              <Row className="g-3 mt-0">
                {/* کارت عمومی */}
                <Col xs={12} md={4} lg={3} xxl={2}>
                  <div className="p-3 shadow-sm card" style={{ background: '#fff' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-secondary fw-bold fs-6">عمومی</small>
                      <i className="material-symbols-outlined">account_balance_wallet</i>
                    </div>
                    <div className="fs-6">کیف پول: {Number(walletBalance).toLocaleString()} تومان</div>
                    <div className="fs-6">اعتبار: {Number(creditBalance).toLocaleString()} تومان</div>
                  </div>
                </Col>

                {/* کارت دسته‌بندی‌ها */}
                {categoryBalances?.map(c => (
                  <Col xs={12} md={4} lg={3} xxl={2} key={c.categoryId}>
                    <div className="p-3 shadow-sm card" style={{ background: '#f5f5f5' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-secondary fw-bold fs-6">{c.categoryName}</small>
                        <i className="material-symbols-outlined">category</i>
                      </div>
                      <div className="fs-6">کیف پول: {Number(c.walletBalance ?? 0).toLocaleString()} تومان</div>
                      <div className="fs-6">اعتبار: {Number(c.creditBalance ?? 0).toLocaleString()} تومان</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Collapse>
          
        </Row>

        {/* بخش دوم: حساب فروشگاه فرهنگیان (با استفاده از Accordion برای مینیمال بودن) */}
        <h5 className="fw-bold mb-3 mt-5 text-dark">وضعیت حساب فروشگاه فرهنگیان</h5>
        <Row><Col xs={12} md={10} lg={8} xxl={6}>
          <Accordion className="rounded-4 overflow-hidden shadow-sm w-lg-50">
            <Accordion.Item eventKey="0" className="bg-warning">
              <Accordion.Header>
                <div className="w-100 pe-3 border-bottom d-flex justify-content-between">
                  <span className="text-start">جمع حساب (1405)</span>
                  <span className="fw-bold fs-4" style={{color: totalShopAll ? getTxColor(shopAll) : "#6c757d"}}>
                    {totalShopAll ? (
                      <>
                        {/*getTxLabel(shopAll)*/} {totalShopAll.toLocaleString()} تومان
                      </>
                    ) : (
                      <>بدون گردش</>
                    )}
                  </span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="p-3 border-bottom text-secondary d-flex justify-content-between">
                  <span>مانده حساب فروشگاه تعاونی</span>
                  <small className="text-muted">{shopAll ? formatJalaliMinusOneDay(shopAll?.createdAt) : '--'}</small>
                  <span>{Number(shopAll?.amount ?? 0).toLocaleString()} تومان</span>
                </div>
                {categoryBalances.filter(c => c.lastShopAll).map(c => {
                  const tx = parseMaybeJson(c.lastShopAll);
                  return (
                    <div key={c.categoryId} className="p-3 border-bottom text-secondary small d-flex justify-content-between">
                      <span>{c.categoryName}</span>
                      <small className="text-muted">{tx ? formatJalaliMinusOneDay(tx?.createdAt) : '--'}</small>
                      <span>{Number(tx?.amount ?? 0).toLocaleString()} تومان</span>
                    </div>
                  );
                })}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1" className="bg-light">
              <Accordion.Header>
                <div className="w-100 pe-3 border-bottom d-flex justify-content-between">
                  <span className="text-start">مانده حساب سال قبل</span>
                  <small className="text-muted">{shopAllOld ? formatJalaliMinusOneDay(shopAllOld?.createdAt) : '--'}</small>
                  <span className="fw-bold fs-4 text-secondary">{(shopAllOld?.amount ?? 0).toLocaleString()} تومان</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3 text-muted small">اطلاعات سال قبل جهت تسویه حساب</Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <div className="w-100 pe-3 border-bottom d-flex justify-content-between">
                  <span className="text-start">جمع کسر ماهانه</span>
                  <span className="fw-bold fs-4 text-secondary">{totalKasreMahane.toLocaleString()} تومان</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="p-3 border-bottom d-flex justify-content-between">
                  <span>حساب جاری</span>
                  <small className="text-muted">{kasreMahane ? formatJalaliMinusOneDay(kasreMahane?.createdAt) : '--'}</small>
                  <span>{Number(kasreMahane?.amount ?? 0).toLocaleString()} تومان</span>
                </div>
                {categoryBalances.filter(c => c.lastKasreMahane).map(c => {
                  const tx = parseMaybeJson(c.lastKasreMahane);
                  return (
                    <div key={c.categoryId} className="p-3 border-bottom small d-flex justify-content-between">
                      <span>{c.categoryName}</span>
                      <small className="text-muted">{tx ? formatJalaliMinusOneDay(tx?.createdAt) : '--'}</small>
                      <span>{Number(tx?.amount ?? 0).toLocaleString()} تومان</span>
                    </div>
                  );
                })}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col></Row>
      </div>





      <Card className="border-0 rounded-3">
        <Card.Body>
          <h5 className="fw-semibold mb-3">درخواست‌های دریافتی از فروشندگان</h5>
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>فروشنده</th>
                <th>تاریخ</th>
                <th>کیف پول</th>
                <th>اعتبار</th>
                <th>توضیحات</th>
                <th>وضعیت</th>
                <th className="d-none">اقدامات</th>
              </tr>
            </thead>
            <tbody>
              {requests.length > 0 ? (
                requests.map((req, i) => (
                  <tr key={req.id}>
                    <td>{i + 1}</td>
                    <td>{req.store?.name}</td>
                    <td>{formatJalali(req.createdAt)}</td>
                    <td>{Number(req.walletAmount || 0).toLocaleString()} تومان</td>
                    <td>{Number(req.creditAmount || 0).toLocaleString()} تومان</td>
                    <td>{req.description}</td>
                    <td>
                      {req.status === "PENDING" && (
                        <span className="badge bg-warning text-dark">در انتظار</span>
                      )}
                      {req.status === "APPROVED" && (
                        <span className="badge bg-success">تایید شده</span>
                      )}
                      {req.status === "REJECTED" && (
                        <span className="badge bg-danger">رد شده</span>
                      )}
                    </td>
                    <td className="d-none">
                      {req.status === "PENDING" ? (
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => handleApprove(req.id)}
                            disabled={actionLoading === req.id}
                          >
                            {actionLoading === req.id ? "..." : "تایید"}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleReject(req.id)}
                            disabled={actionLoading === req.id}
                          >
                            {actionLoading === req.id ? "..." : "رد"}
                          </Button>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    هیچ درخواستی وجود ندارد
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

export default UserCreditRequestsComp;
